import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Col,
  Layout,
  notification,
  Row,
  Skeleton,
  Alert,
  Space,
  Tabs,
  Typography,
  Descriptions,
  Popconfirm,
} from 'antd';

import LoadTemplateForm from '../../containers/LoadTemplateForm';
import LoadTemplateProcessForm from '../../containers/LoadTemplateProcessForm';
import BreadcrumbContainer from '../../containers/Breadcrumb';
import CustomButton from '../../components/CustomButton';
import PdfViewer from '../../components/PdfViewer';
import INTERNAL_LINKS from '../../enums/InternalLinks';
import MESSAGES from '../../enums/Messages';
import {
  changeStatus,
  filter as filterVersions,
  info as infoTemplate,
  templateTypeTable,
} from '../../api/module/templates';
import { generateTemplateURL } from '../../api/module/template';

import { getAll as getAllHiringStages } from '../../api/module/hiring_stages';
import { get as getDocument } from '../../api/module/document_repository';

import './style.scss';
import Paragraph from 'antd/es/typography/Paragraph';
import AjaxTable from '../../components/AjaxTable';
import {
  catchException,
  catchExceptionSRate,
} from '../../utils/helpers/helpers';

const { Content } = Layout;
const { Title } = Typography;

const { TabPane } = Tabs;

const TemplateTable = ({
  hiring_type_id,
  hiring_stage_id,
  person_type_id,
  contract_type_id,
  template_type_id,
  showCLick,
  td,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const [reloadSource, setReloadSource] = useState(false);
  const [activeID, setActiveID] = useState(td.activo);

  const [hasOld, setHasOld] = useState(1);

  const generateTemplatePDF = async (version) => {
    try {
      setPreviewLoading(true);
      let response = await generateTemplateURL({
        template: template_type_id,
        version: version,
      });

      showCLick(response.data.url);
      setPreviewLoading(false);
    } catch (exception) {
      setPreviewLoading(false);
      catchExceptionSRate(
        exception,
        'No se pudo generar el PDF. Revise los nombres de las variables utilizadas.',
      );
    }
  };

  const getDocumentURL = async (document) => {
    try {
      setDownloadLoading(true);
      let response = await getDocument(document);
      window.open(response.data.url);
      setDownloadLoading(false);
    } catch (exception) {
      setDownloadLoading(false);
      catchExceptionSRate(exception, MESSAGES.DESC_ERROR_GET_MSG);
    }
  };

  const changeTemplateStatus = async (template) => {
    try {
      setStatusLoading(true);
      let response = await changeStatus(template.id);
      if (response.data.estado) setActiveID(response.data.id);
      else if (response.data.id === activeID) setActiveID();
      setStatusLoading(false);
    } catch (exception) {
      setStatusLoading(false);
      catchExceptionSRate(exception, MESSAGES.DESC_ERROR_GET_MSG);
    }
  };

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'created_at',
      render: (t) => dayjs(t).format('YYYY-MM-DD'),
      align: 'center',
    },
    {
      title: 'Versión',
      dataIndex: 'version',
      align: 'center',
    },
    {
      title: 'Cambia logo',
      dataIndex: 'reemplazo_logo',
      render: (t) => (t ? 'Si' : 'No'),
      align: 'center',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: '20rem',
      fixed: 'right',
      align: 'center',
      render: (_, record) => {
        return (
          <Row gutter={8}>
            <Col span={7}>
              <CustomButton
                block
                text="Ver PDF"
                loading={previewLoading}
                onClick={() => generateTemplatePDF(record.version)}
              />
            </Col>
            <Col span={8}>
              <CustomButton
                block
                loading={downloadLoading}
                type="default"
                text={'Descargar'}
                onClick={() => getDocumentURL(record.documento)}
              />
            </Col>
            <Col span={9}>
              <CustomButton
                block
                danger
                type="default"
                loading={statusLoading}
                text={activeID === record.id ? 'Desactivar' : 'Activar'}
                onClick={() => changeTemplateStatus(record)}
              />
            </Col>
          </Row>
        );
      },
    },
  ];

  const onRefresh = () => {
    setIsModalOpen(false);
    setReloadSource({});
  };

  return (
    <Content>
      <Descriptions layout="vertical" column={2} bordered>
        <Descriptions.Item label="Código" style={{ width: '50%' }}>
          {td.codigo}
        </Descriptions.Item>
        <Descriptions.Item label="Categoría" style={{ width: '50%' }}>
          {td.categoria}
        </Descriptions.Item>
        <Descriptions.Item label="Nombre" span={2}>
          {td.nombre}
        </Descriptions.Item>
      </Descriptions>
      <br />
      {hasOld ? (
        <Alert
          message="Nota"
          description="Recuerde que cualquier cambio a la plantilla debe hacerse sobre la última versión activa en SIGA. De no seguir esta instrucción, dañará la plantilla y generará errores en todos los documentos del proceso contractual"
          type="error"
          showIcon
        />
      ) : null}
      <AjaxTable
        columns={columns}
        tableTitle="Listado de Plantillas"
        scroll={{ x: 300 }}
        reloadSource={reloadSource}
        endpoint={filterVersions}
        pageSize={50}
        query={`/?filter[tipo_plantilla]=${template_type_id}`}
        setCount={setHasOld}
      />
      <Space style={{ marginTop: 10 }}>
        {hasOld ? (
          <Popconfirm
            placement="topLeft"
            title="¿Usted tomó la última versión activa de la plantilla en SIGA para hacer los cambios requeridos?"
            okText="Sí"
            cancelText="No"
            onConfirm={() => setIsModalOpen(true)}
          >
            <CustomButton text="Cargar nueva versión" />
          </Popconfirm>
        ) : (
          <CustomButton
            onClick={() => setIsModalOpen(true)}
            text="Cargar plantilla"
          />
        )}
        {hiring_type_id === 0 &&
          hiring_stage_id === 0 &&
          contract_type_id === 0 && (
            <CustomButton
              type={'default'}
              className={'back-button'}
              htmlType="button"
              text={
                <Link to={`${INTERNAL_LINKS.TEMPLATES}`}>
                  Volver a plantillas
                </Link>
              }
            />
          )}
        {hiring_type_id !== 0 &&
          hiring_stage_id !== 0 &&
          contract_type_id !== 0 && (
            <CustomButton
              type={'default'}
              className={'back-button'}
              htmlType="button"
              text={
                <Link
                  to={`${INTERNAL_LINKS.TEMPLATE}/${hiring_type_id}/0/${contract_type_id}/${person_type_id}/0`}
                >
                  Volver a etapa de contratación
                </Link>
              }
            />
          )}
      </Space>
      <LoadTemplateForm
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        template_type_id={template_type_id}
        onRefresh={onRefresh}
      />
    </Content>
  );
};

const TemplatePreList = ({ hiring_type_id, person_type_id, contract_id }) => {
  const [isModalNewOpen, setIsModalNewOpen] = useState(false);
  const [hiringStage, setHiringStage] = useState(null);
  const [hiring_stage_id, setHiringStageID] = useState(1);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  const [reloadSource, setReloadSource] = useState(false);

  useEffect(() => {
    async function getHiringTypes() {
      try {
        let response = await getAllHiringStages();
        setLoadingTemplates(false);
        setHiringStage(response.data.results);
      } catch (exception) {
        catchExceptionSRate(exception, MESSAGES.DESC_ERROR_GET_MSG);
      }
    }
    getHiringTypes();
  }, []);

  const setTab = (key) => {
    setHiringStageID(key);
  };

  const columns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      width: '12rem',
      align: 'center',
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
    },
    {
      title: 'Categoría',
      dataIndex: 'categoria_nombre',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 120,
      render: (_, record) => (
        <CustomButton
          htmlType="button"
          text={
            <Link
              to={`${INTERNAL_LINKS.TEMPLATE}/${hiring_type_id}/${hiring_stage_id}/${contract_id}/${person_type_id}/${record.id}`}
            >
              Ver plantilla
            </Link>
          }
        />
      ),
    },
  ];

  return (
    <Skeleton loading={loadingTemplates} active>
      <Tabs defaultActiveKey="1" onTabClick={setTab} type="card">
        {hiringStage != null &&
          hiringStage.map((item) => (
            <TabPane tab={item.nombre} key={item.id}>
              <Row gutter={[12, 12]}>
                <Col span={24}>
                  <Paragraph className={'mb-0'}>
                    Plantillas {item.nombre.toLowerCase()}
                  </Paragraph>
                </Col>
                <Col span={24}>
                  <AjaxTable
                    columns={columns}
                    query={{
                      tipo_contrato__isnull: false,
                      tipo_contrato: contract_id,
                      tipo_persona: person_type_id,
                      etapa_contratacion: item.id,
                    }}
                    endpoint={templateTypeTable}
                    reloadSource={reloadSource}
                  />
                </Col>
              </Row>
              {item.nombre.toLowerCase() == 'contractual' && (
                <Row gutter={[12, 12]}>
                  <Col span={24}>
                    <Paragraph className={'mb-0'}></Paragraph>
                  </Col>
                  <Col span={24}>
                    <CustomButton
                      onClick={() => setIsModalNewOpen(true)}
                      text="Crear nueva plantilla"
                    />
                    <LoadTemplateProcessForm
                      isModalNewOpen={isModalNewOpen}
                      setIsModalNewOpen={setIsModalNewOpen}
                      person_type_id={person_type_id}
                      contract_type_id={contract_id}
                      onRefresh={() => {
                        setReloadSource({});
                      }}
                    />
                  </Col>
                </Row>
              )}
            </TabPane>
          ))}
      </Tabs>
    </Skeleton>
  );
};

export default function TemplateType({ match }) {
  const [hiringType] = useState(parseInt(match.params.hiring_type_id));
  const [hiringStage] = useState(parseInt(match.params.hiring_stage_id));
  const [contractType] = useState(parseInt(match.params.contract_type_id));
  const [personType] = useState(parseInt(match.params.person_type_id));
  const [templateTypeId] = useState(parseInt(match.params.template_type_id));

  const [info, setInfo] = useState();

  const [showPdf, setShowPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');

  const showCLick = (url) => {
    setPdfUrl(url);
    setShowPdf((s) => !s);
  };

  const getInfo = async () => {
    try {
      const response = await infoTemplate({
        tipo_plantilla: templateTypeId,
        tipo_persona: personType,
        tipo_contrato: contractType,
        tipo_contratacion: hiringType,
        etapa_contratacion: hiringStage,
      });
      setInfo({
        bc: response.data.bc.map((v) => (v === '0' ? undefined : v)),
        td: response.data.td,
      });
    } catch (exception) {
      console.log(exception);
      let description;

      if (exception.response.data.errors) {
        const errors = exception.response.data.errors;
        description = errors.shift().detail;
      } else {
        description = 'No se pudieron obtener los registros.';
      }

      notification.error({
        message: MESSAGES.ERROR_MSG,
        description: description,
        duration: 2,
      });
    }
  };

  useEffect(() => {
    getInfo();
  }, []);

  const breadcrumbItems = [
    {
      text: 'Plantillas',
      to: `${INTERNAL_LINKS.TEMPLATES}`,
    },
    {
      text: `${info?.bc[0] ? info.bc[0] + ' - ' : ''}
        ${info?.bc[1] ? info.bc[1] + ' - ' : ''}
        ${info?.bc[2] ? info.bc[2] + ' - ' : ''}
        ${info?.bc[3] ? 'Persona ' + info.bc[3] + ' - ' : 'Cargando ...'}
        ${info?.bc[4] ? info.bc[4] : ''}
        `,
    },
  ];

  return (
    <Content>
      <BreadcrumbContainer items={breadcrumbItems} />
      <Title className={'text-uppercase'} level={2}>
        Módulo de Plantillas
      </Title>
      <Title level={4}>
        {info?.bc[0] ? info.bc[0] + ' - ' : ''}
        {info?.bc[1] ? info.bc[1] + ' - ' : ''}
        {info?.bc[2] ? info.bc[2] + ' - ' : ''}
        {info?.bc[3] ? 'Persona ' + info.bc[3] : 'Cargando ...'}
        {info?.bc[4] ? ' - ' + info.bc[4] : ''}
      </Title>
      <Row gutter={12}>
        <Col span={24}>
          <Alert
            style={{ marginBottom: 15 }}
            message="Importante"
            action={
              <CustomButton
                type={'default'}
                className={'back-button'}
                htmlType="button"
                text={
                  <a
                    href={`${process.env.REACT_APP_API_DOMAIN}/static/download/DiccionarioVariables.pdf`}
                    target="_blank"
                  >
                    Descargar diccionario
                  </a>
                }
              />
            }
            description={
              <Paragraph style={{ marginBottom: 0 }}>
                Para crear o cargar una nueva plantilla, descargue el
                diccionario de variables.
              </Paragraph>
            }
            type="info"
            showIcon
          />
        </Col>
      </Row>
      {info && (
        <>
          {templateTypeId === 0 ? (
            <div style={{ marginTop: '2rem' }}>
              <Row gutter={12}>
                <Col span={24}>
                  <TemplatePreList
                    hiring_type_id={hiringType}
                    person_type_id={personType}
                    contract_id={contractType}
                  />
                </Col>
                <Col span={24}>
                  <CustomButton
                    style={{ marginTop: 15 }}
                    type={'default'}
                    className={'back-button'}
                    htmlType="button"
                    text={<Link to={INTERNAL_LINKS.TEMPLATES}> Volver </Link>}
                  />
                </Col>
              </Row>
            </div>
          ) : (
            <TemplateTable
              hiring_type_id={hiringType}
              hiring_stage_id={hiringStage}
              person_type_id={personType}
              template_type_id={templateTypeId}
              contract_type_id={contractType}
              td={info.td}
              showCLick={showCLick}
            />
          )}
        </>
      )}

      <PdfViewer
        pdf={pdfUrl}
        onCancel={() => setShowPdf(false)}
        open={showPdf}
        isWord={true}
      />
    </Content>
  );
}
